import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { LanguageSwitch } from '../components/LanguageSwitch';
import { api } from '../services/api';

function Dashboard() {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [jobsError, setJobsError] = useState('');

  const loadJobs = useCallback(async () => {
    try {
      setJobsError('');
      const response = await api.get('/jobs');
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Network error';
      setJobsError(t('dashboard.errorLoadJobs') + (msg ? ' ' + msg : ''));
    } finally {
      setLoadingJobs(false);
    }
  }, [t]);

  useEffect(() => {
    loadJobs();
    // Poll for job updates every 5 seconds
    const interval = setInterval(loadJobs, 5000);
    return () => clearInterval(interval);
  }, [loadJobs]);

  const inferContentType = (selectedFile) => {
    if (selectedFile?.type) return selectedFile.type;
    const extension = selectedFile?.name?.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return 'application/pdf';
    if (extension === 'zip') return 'application/zip';
    return '';
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const allowedTypes = ['application/pdf', 'application/zip', 'application/x-zip-compressed'];
      const extension = selectedFile.name.split('.').pop()?.toLowerCase() || '';
      if (!allowedTypes.includes(selectedFile.type) && !['pdf', 'zip'].includes(extension)) {
        setError(t('dashboard.fileTypeError'));
        return;
      }
      if (selectedFile.size > 50 * 1024 * 1024) {
        setError(t('dashboard.fileSizeError'));
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError(t('dashboard.selectFile'));
      return;
    }

    setUploading(true);
    setError('');

    try {
      // Step 1: Get presigned URL
      const inferredContentType = inferContentType(file);
      const presignedResponse = await api.post('/presigned-url', {
        filename: file.name,
        contentType: inferredContentType,
      });

      const { presignedUrl, s3Key } = presignedResponse.data;

      // Step 2: Upload file to S3 (must succeed before we create the document)
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': inferredContentType,
        },
      });
      if (!uploadResponse.ok) {
        const errText = await uploadResponse.text();
        throw new Error(`Upload failed (${uploadResponse.status}): ${errText || uploadResponse.statusText}`);
      }

      // Step 3: Create document record
      const documentResponse = await api.post('/documents', {
        s3Key,
        originalFilename: file.name,
        mimeType: inferredContentType,
      });

      const { documentId } = documentResponse.data;

      // Step 4: Create analysis job
      await api.post('/jobs', {
        s3Key,
        documentId,
      });

      // Reset form and reload jobs
      setFile(null);
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) fileInput.value = '';
      await loadJobs();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.response?.data?.error || err.message || t('dashboard.uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'done':
        return '#10b981';
      case 'processing':
        return '#3b82f6';
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6' }}>
      <header style={{
        background: 'white',
        padding: '1rem 2rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <h1 style={{ margin: 0, color: '#333' }}>CreditCharges</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <LanguageSwitch />
          <span style={{ color: '#666' }}>{user?.email}</span>
          <button
            onClick={logout}
            style={{
              padding: '0.5rem 1rem',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            {t('common.logout')}
          </button>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          marginBottom: '2rem'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#333' }}>
            Upload Contract
          </h2>

          {error && (
            <div style={{
              padding: '0.75rem',
              marginBottom: '1rem',
              background: '#fee',
              color: '#c33',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}>
              {error}
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <input
              type="file"
              accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed"
              onChange={handleFileChange}
              disabled={uploading}
              style={{
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem',
                width: '100%',
                boxSizing: 'border-box'
              }}
            />
          </div>

          {file && (
            <div style={{ marginBottom: '1rem', color: '#666' }}>
              Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            style={{
              padding: '0.75rem 1.5rem',
              background: (!file || uploading) ? '#ccc' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: (!file || uploading) ? 'not-allowed' : 'pointer'
            }}
          >
            {uploading ? 'Uploading...' : 'Upload & Analyze'}
          </button>
        </div>

        <div style={{
          background: 'white',
          padding: '2rem',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#333' }}>
            Analysis Jobs
          </h2>

          {jobsError ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#b91c1c', background: '#fef2f2', borderRadius: '8px', marginBottom: '1rem' }}>
              {jobsError}
            </div>
          ) : null}
          {loadingJobs ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              Loading jobs...
            </div>
          ) : jobs.length === 0 && !jobsError ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
              No jobs yet. Upload a contract to get started.
            </div>
          ) : jobs.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {jobs.map((job) => (
                <div
                  key={job.jobId}
                  onClick={() => navigate(`/jobs/${job.jobId}`)}
                  style={{
                    padding: '1rem',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                  }}
                >
                  <div>
                    <div style={{ fontWeight: '500', color: '#333', marginBottom: '0.25rem' }}>
                      Job {job.jobId.substring(0, 8)}...
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#666' }}>
                      Created: {new Date(job.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '12px',
                    background: getStatusColor(job.status) + '20',
                    color: getStatusColor(job.status),
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    textTransform: 'capitalize'
                  }}>
                    {job.status}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

export default Dashboard;

